/**
 * Saved Views API Service
 *
 * CRUD and lifecycle operations for saved views.
 * Follows the propertyService pattern — async methods, unwrap data.
 * @see http://localhost:8080/docs-json
 */

import api from '@/core/api'
import type { PaginatedResponse } from '@/core/pagination'
import type {
    SavedView,
    CreateSavedViewDto,
    UpdateSavedViewDto,
    SavedViewListParams,
    PreviewRequest,
    CountResponse,
    EntitySchema,
    ApiResponse,
    FieldValuesResponse,
} from './types'

export const savedViewService = {
    // ─── Schema ─────────────────────────────────────────────────────

    /**
     * List available entity schemas
     * @see OpenAPI: GET /saved-views/schema
     */
    async getSchemaList(): Promise<EntitySchema[]> {
        const { data } = await api.get<ApiResponse<EntitySchema[]>>('/saved-views/schema')
        return data.data
    },

    /**
     * Get schema for a specific entity type
     * @see OpenAPI: GET /saved-views/schema/:entityType
     */
    async getSchema(entityType: string): Promise<EntitySchema> {
        const { data } = await api.get<ApiResponse<EntitySchema>>(`/saved-views/schema/${entityType}`)
        return data.data
    },

    /**
     * Get distinct values for a specific field (for searchable dropdowns)
     * @see OpenAPI: GET /saved-views/schema/:entityType/values/:field
     */
    async getFieldValues(
        entityType: string,
        field: string,
        search?: string,
    ): Promise<FieldValuesResponse> {
        const { data } = await api.get<ApiResponse<FieldValuesResponse>>(
            `/saved-views/schema/${entityType}/values/${field}`,
            { params: search ? { search } : undefined },
        )
        return data.data
    },

    // ─── CRUD ───────────────────────────────────────────────────────

    /**
     * Create a new saved view
     * @see OpenAPI: POST /saved-views
     */
    async create(dto: CreateSavedViewDto): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>('/saved-views', dto)
        return data.data
    },

    /**
     * List saved views with optional filters
     * @see OpenAPI: GET /saved-views
     */
    async list(params?: SavedViewListParams): Promise<SavedView[]> {
        const { data } = await api.get<ApiResponse<SavedView[]>>('/saved-views', { params })
        return data.data
    },

    /**
     * Get a single saved view by ID
     * @see OpenAPI: GET /saved-views/:id
     */
    async get(id: string): Promise<SavedView> {
        const { data } = await api.get<ApiResponse<SavedView>>(`/saved-views/${id}`)
        return data.data
    },

    /**
     * Update an existing saved view
     * @see OpenAPI: PATCH /saved-views/:id
     */
    async update(id: string, dto: UpdateSavedViewDto): Promise<SavedView> {
        const { data } = await api.patch<ApiResponse<SavedView>>(`/saved-views/${id}`, dto)
        return data.data
    },

    /**
     * Delete a saved view
     * @see OpenAPI: DELETE /saved-views/:id
     */
    async remove(id: string): Promise<void> {
        await api.delete(`/saved-views/${id}`)
    },

    /**
     * Duplicate a saved view
     * @see OpenAPI: POST /saved-views/:id/duplicate
     */
    async duplicate(id: string): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>(`/saved-views/${id}/duplicate`)
        return data.data
    },

    // ─── Default View Preferences ───────────────────────────────────

    /**
     * Get default landing view for an entity type (user preference with system fallback)
     * @see OpenAPI: GET /saved-views/default/:entityType
     */
    async getDefault(entityType: string): Promise<SavedView | null> {
        const { data } = await api.get<ApiResponse<SavedView | null>>(`/saved-views/default/${entityType}`)
        return data.data
    },

    /**
     * Set a saved view as the user's default for its entity type
     * @see OpenAPI: POST /saved-views/:id/set-default
     */
    async setDefault(id: string): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>(`/saved-views/${id}/set-default`)
        return data.data
    },

    /**
     * Reset user default view preference for an entity type (reverts to system view)
     * @see OpenAPI: DELETE /saved-views/default/:entityType
     */
    async resetDefault(entityType: string): Promise<void> {
        await api.delete(`/saved-views/default/${entityType}`)
    },

    // ─── Results & Preview ──────────────────────────────────────────

    /**
     * Get paginated results for a saved view
     * @see OpenAPI: GET /saved-views/:id/results
     */
    async getResults(id: string, params?: { page?: number; limit?: number }): Promise<PaginatedResponse<Record<string, unknown>>> {
        const { data } = await api.get<PaginatedResponse<Record<string, unknown>>>(`/saved-views/${id}/results`, { params })
        return data
    },

    /**
     * Get result count for a saved view
     * @see OpenAPI: GET /saved-views/:id/count
     */
    async getCount(id: string): Promise<CountResponse> {
        const { data } = await api.get<ApiResponse<CountResponse>>(`/saved-views/${id}/count`)
        return data.data
    },

    /**
     * Preview filter results without saving
     * @see OpenAPI: POST /saved-views/preview
     */
    async preview(req: PreviewRequest): Promise<PaginatedResponse<Record<string, unknown>>> {
        const { data } = await api.post<PaginatedResponse<Record<string, unknown>>>('/saved-views/preview', req)
        return data
    },

    /**
     * Preview filter results without saving, automatically paginating API calls
     * to fetch all matching results into a single array for 1-page GUI views.
     */
    async previewAll(req: PreviewRequest): Promise<Record<string, unknown>[]> {
        const allResults: Record<string, unknown>[] = []
        let page = 1
        const limit = 100

        while (true) {
            const res = await this.preview({
                ...req,
                page,
                limit,
            })
            const items = res.data || []
            allResults.push(...items)

            if (
                items.length === 0 ||
                !res.meta?.hasNext ||
                (typeof res.meta?.totalPages === 'number' && page >= res.meta.totalPages)
            ) {
                break
            }
            page++
        }

        return allResults
    },

    // ─── Lifecycle ──────────────────────────────────────────────────

    /**
     * Freeze a dynamic view into a static snapshot
     * @see OpenAPI: POST /saved-views/:id/freeze
     */
    async freeze(id: string): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>(`/saved-views/${id}/freeze`)
        return data.data
    },

    /**
     * Unfreeze a static view back to dynamic
     * @see OpenAPI: POST /saved-views/:id/unfreeze
     */
    async unfreeze(id: string): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>(`/saved-views/${id}/unfreeze`)
        return data.data
    },

    // ─── Static View Item Management ────────────────────────────────

    /**
     * Add entity IDs to a static view
     * @see OpenAPI: POST /saved-views/:id/items
     */
    async addItems(id: string, entityIds: string[]): Promise<SavedView> {
        const { data } = await api.post<ApiResponse<SavedView>>(`/saved-views/${id}/items`, { entityIds })
        return data.data
    },

    /**
     * Remove entity IDs from a static view
     * @see OpenAPI: DELETE /saved-views/:id/items
     */
    async removeItems(id: string, entityIds: string[]): Promise<SavedView> {
        const { data } = await api.delete<ApiResponse<SavedView>>(`/saved-views/${id}/items`, { data: { entityIds } })
        return data.data
    },
}
